import rolePermission from '@/constants/rolePermission';
import { DEFAULT_LAYOUT } from '../base';
import { AppRouteRecordRaw } from '../types';

const Goods: AppRouteRecordRaw = {
  path: '/goods',
  name: 'Goods',
  component: DEFAULT_LAYOUT,
  meta: {
    title: '商品管理',
    requiresAuth: true,
    icon: 'icon-skin',
    order: 2,
  },
  children: [
    {
      path: 'index',
      name: 'GoodsIndex',
      component: () => import('@/views/goods/index.vue'),
      meta: {
        title: '商品信息',
        requiresAuth: true,
        roles: rolePermission.goodsGet,
        permissionMode: 'some',
        menuIcon: 'icon-spgl',
      },
    },
    {
      path: 'add-goods',
      name: 'AddGoods',
      component: async () => {
        const cpn = await import('@/views/goods/add-or-edit.vue');
        return { ...cpn.default, name: 'AddGoods' };
      },
      props: () => ({
        pageType: 'goods',
      }),
      meta: {
        title: '新增商品',
        requiresAuth: true,
        roles: ['goodsEdit'],
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'edit-goods',
      name: 'EditGoods',
      component: async () => {
        const cpn = await import('@/views/goods/add-or-edit.vue');
        return { ...cpn.default, name: 'EditGoods' };
      },
      props: (route: any) => ({
        pageType: 'goods',
        id: route.query.id, // 商品id
        formRouteName: route.query.formRouteName,
        produceOrdersSn: route.query.produceOrdersSn, // 生产单单号
      }),
      meta: {
        title: '编辑商品',
        requiresAuth: true,
        roles: rolePermission.goodsGet,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'import-goods',
      name: 'ImportGoods',
      component: async () => {
        const cpn = await import('@/views/goods/importGoods/index.vue');
        return { ...cpn.default, name: 'ImportGoods' };
      },
      meta: {
        title: '导入商品',
        requiresAuth: true,
        roles: ['goodsEdit'],
        hideInMenu: true,
      },
    },
    {
      path: 'finished-in-order',
      name: 'FinishedInOrder',
      component: () => import('@/views/goods/finishedInOrder/index.vue'),
      meta: {
        title: '成品入库',
        requiresAuth: true,
        roles: ['goodsStoreInOrderGet'],
        menuIcon: 'icon-chengpinruku',
      },
    },
    {
      path: 'finished-in-order-edit',
      name: 'EditFinishedInOrder',
      component: async () => {
        const cpn = await import('@/views/goods/finishedInOrder/addOrEdit.vue');
        return { ...cpn.default, name: 'EditFinishedInOrder' };
      },
      meta: {
        title: '成品入库单详情',
        requiresAuth: true,
        roles: ['goodsStoreInOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'finished-out-order',
      name: 'FinishedOutOrder',
      component: () => import('@/views/goods/finishedOutOrder/index.vue'),
      meta: {
        title: '成品出库',
        requiresAuth: true,
        roles: ['goodsStoreOutOrderGet'],
        menuIcon: 'icon-chengpinchuku',
      },
    },
    {
      path: 'finished-out-order-edit',
      name: 'EditFinishedOutOrder',
      component: async () => {
        const cpn = await import(
          '@/views/goods/finishedOutOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditFinishedOutOrder' };
      },
      meta: {
        title: '成品出库单详情',
        requiresAuth: true,
        roles: ['goodsStoreOutOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'finish-transfer',
      name: 'FinishTransfer',
      component: () => import('@/views/goods/finishTransfer/index.vue'),
      meta: {
        title: '成品调拨',
        requiresAuth: true,
        roles: ['goodsStoreTransferOrderGet'],
        menuIcon: 'icon-chengpintiaobo',
      },
    },
    {
      path: 'finish-transfer-edit',
      name: 'EditFinishTransfer',
      component: async () => {
        const cpn = await import('@/views/goods/finishTransfer/addOrEdit.vue');
        return { ...cpn.default, name: 'EditFinishTransfer' };
      },
      meta: {
        title: '编辑成品调拨单',
        requiresAuth: true,
        roles: ['goodsStoreTransferOrderGet'],
        notOnline: true,
        hideInMenu: true,
      },
    },
    {
      path: 'finish-transfer-detail',
      name: 'DetailFinishTransfer',
      component: async () => {
        const cpn = await import('@/views/goods/finishTransfer/addOrEdit.vue');
        return { ...cpn.default, name: 'EditFinishTransfer' };
      },
      meta: {
        title: '成品调拨单详情',
        requiresAuth: true,
        roles: ['goodsStoreTransferOrderGet'],
        notOnline: true,
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'finish-transfer-add',
      name: 'AddFinishTransfer',
      component: async () => {
        const cpn = await import('@/views/goods/finishTransfer/addOrEdit.vue');
        return { ...cpn.default, name: 'AddFinishTransfer' };
      },
      meta: {
        title: '新增成品调拨单',
        requiresAuth: true,
        roles: rolePermission.goodsStoreTransferOrderEdit,
        permissionMode: 'some',
        notOnline: true,
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'finish-checked',
      name: 'FinishChecked',
      component: () => import('@/views/goods/finishChecked/index.vue'),
      meta: {
        title: '成品盘点',
        requiresAuth: true,
        roles: ['goodsStoreOrderGet'],
        menuIcon: 'icon-chengpinpandian',
      },
    },
    {
      path: 'finish-checked-edit',
      name: 'EditFinishChecked',
      component: async () => {
        const cpn = await import('@/views/goods/finishChecked/addOrEdit.vue');
        return { ...cpn.default, name: 'EditFinishChecked' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑成品盘点',
        requiresAuth: true,
        roles: ['goodsStoreOrderGet'],
        notOnline: true,
        hideInMenu: true,
      },
    },
    {
      path: 'finish-checked-add',
      name: 'AddFinishChecked',
      component: async () => {
        const cpn = await import('@/views/goods/finishChecked/addOrEdit.vue');
        return { ...cpn.default, name: 'AddFinishChecked' };
      },
      meta: {
        title: '新增成品盘点',
        requiresAuth: true,
        roles: rolePermission.goodsStoreOrderEdit,
        permissionMode: 'some',
        notOnline: true,
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'finish-checked-details',
      name: 'FinishCheckedDetails',
      component: async () => {
        const cpn = await import('@/views/goods/finishChecked/addOrEdit.vue');
        return { ...cpn.default, name: 'FinishCheckedDetails' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '成品盘点详情',
        requiresAuth: true,
        roles: ['goodsStoreOrderGet'],
        notOnline: true,
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'finish-stock-detail',
      name: 'FinishStockDetail',
      component: () => import('@/views/goods/finishStockDetail/index.vue'),
      meta: {
        title: '成品库存流水',
        requiresAuth: true,
        roles: ['goodsStoredRecordGet'],
        menuIcon: 'icon-kcls',
      },
    },
    {
      path: 'sample-business-print',
      name: 'SampleBusinessTemplate',
      component: () => import('@/views/goods/importGoods/sample-template.vue'),
      props: (route: any) => ({
        type: route.query.type || 'sampleOrderTemplate',
        id: route.query.id,
        pageSource: route.query.pageSource,
        colorIds: route.query.colorIds,
      }),
      meta: {
        title: '样版单打印',
        requiresAuth: false,
        hideInMenu: true,
        iframe: true,
      },
    },
    {
      path: 'printPriceOrder',
      name: 'PrintPriceOrder',
      component: () => import('@/views/goods/printPriceOrder/index.vue'),
      props: (route: any) => ({
        goodsId: route.query.goodsId,
        colorId: route.query.colorId,
      }),
      meta: {
        title: '核价单',
        requiresAuth: false,
        hideInMenu: true,
        roles: ['pricePrint'],
      },
    },
  ],
};

export default Goods;
